import React, { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';

type LoadState = 'loading' | 'ready' | 'error';

const TERMS_OF_SERVICE_PATH = `${process.env.PUBLIC_URL || ''}/terms_of_services.md`;

const TermsOfService: React.FC = () => {
  const [markdown, setMarkdown] = useState<string>('');
  const [state, setState] = useState<LoadState>('loading');

  useEffect(() => {
    let isMounted = true;

    const loadMarkdown = async () => {
      try {
        const response = await fetch(TERMS_OF_SERVICE_PATH, { cache: 'no-cache' });
        if (!response.ok) {
          throw new Error('Failed to fetch terms of service');
        }
        const text = await response.text();
        if (isMounted) {
          setMarkdown(text);
          setState('ready');
        }
      } catch (error) {
        if (isMounted) {
          setState('error');
        }
      }
    };

    loadMarkdown();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <Box component="article" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h3" component="h1">
        Terms of Service
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Review the rules and obligations that govern your use of the R2B Platform.
      </Typography>
      {state === 'loading' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading terms of service…</Typography>
        </Box>
      )}
      {state === 'error' && (
        <Alert severity="error">
          We could not load the terms of service. Please refresh the page or contact support if the problem persists.
        </Alert>
      )}
      {state === 'ready' && (
        <Typography
          component="pre"
          sx={{
            whiteSpace: 'pre-wrap',
            fontFamily: theme => theme.typography.body1.fontFamily,
            fontSize: theme => theme.typography.body1.fontSize,
            lineHeight: 1.7,
            mt: 1,
          }}
        >
          {markdown}
        </Typography>
      )}
    </Box>
  );
};

export default TermsOfService;
